import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';

const SiteSettingsContext = createContext();

export const useSiteSettings = () => {
  return useContext(SiteSettingsContext);
};

const ICON_MAP = {};

const loadIconMap = async () => {
  if (Object.keys(ICON_MAP).length > 0) return;
  const icons = await import('@ant-design/icons');
  ICON_MAP.HomeOutlined = icons.HomeOutlined;
  ICON_MAP.FileTextOutlined = icons.FileTextOutlined;
  ICON_MAP.TeamOutlined = icons.TeamOutlined;
  ICON_MAP.GlobalOutlined = icons.GlobalOutlined;
  ICON_MAP.PhoneOutlined = icons.PhoneOutlined;
  ICON_MAP.MailOutlined = icons.MailOutlined;
  ICON_MAP.InfoCircleOutlined = icons.InfoCircleOutlined;
  ICON_MAP.BookOutlined = icons.BookOutlined;
  ICON_MAP.SafetyOutlined = icons.SafetyOutlined;
  ICON_MAP.AuditOutlined = icons.AuditOutlined;
  ICON_MAP.BankOutlined = icons.BankOutlined;
  ICON_MAP.SolutionOutlined = icons.SolutionOutlined;
  ICON_MAP.ProfileOutlined = icons.ProfileOutlined;
  ICON_MAP.ReadOutlined = icons.ReadOutlined;
  ICON_MAP.FormOutlined = icons.FormOutlined;
  ICON_MAP.FileProtectOutlined = icons.FileProtectOutlined;
  ICON_MAP.SearchOutlined = icons.SearchOutlined;
  ICON_MAP.EditOutlined = icons.EditOutlined;
  ICON_MAP.FolderOutlined = icons.FolderOutlined;
  ICON_MAP.BarsOutlined = icons.BarsOutlined;
  ICON_MAP.LinkOutlined = icons.LinkOutlined;
};

const DEFAULT_NAV_LINKS = [
  { to: '/', label: 'الرئيسية', icon: 'HomeOutlined', enabled: true },
  { to: '/news', label: 'أعمالنا', icon: 'FileTextOutlined', enabled: true },
  { to: '/reports', label: 'التقارير', icon: 'GlobalOutlined', enabled: true },
  { to: '/sections', label: 'الأقسام', icon: 'TeamOutlined', enabled: true },
  { to: '/about-us', label: 'من نحن', icon: 'InfoCircleOutlined', enabled: true },
  { to: '/contact-us', label: 'اتصل بنا', icon: 'PhoneOutlined', enabled: true },
];

const getContrastText = (hex) => {
  if (!hex || !hex.startsWith('#')) return '#1A1A2E';
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.5 ? '#1A1A2E' : '#ffffff';
};

const getDefaultIcon = (iconName) => {
  switch (iconName) {
    case 'HomeOutlined': return React.createElement('span', null, '🏠');
    case 'FileTextOutlined': return React.createElement('span', null, '📄');
    case 'TeamOutlined': return React.createElement('span', null, '👥');
    case 'GlobalOutlined': return React.createElement('span', null, '🌐');
    case 'PhoneOutlined': return React.createElement('span', null, '📞');
    default: return React.createElement('span', null, '🔗');
  }
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    site_name: '',
    site_logo: '/logo.png',
    site_favicon: '/favicon.ico',
    site_font: "'Tajawal', sans-serif",
    site_language: 'ar',
    site_languages: JSON.stringify(['ar', 'en']),
    siteDescription: '',
    site_keywords: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    youtubeUrl: '',
    tiktokUrl: '',
    whatsappUrl: '',
    callUrl: '',
    snapchatUrl: '',
    headerColor: '#ffffff',
    footerColor: '#0F1B2D',
    secondaryColor: '#C8953A',
    nav_links: JSON.stringify(DEFAULT_NAV_LINKS),
    ticker_enabled: '0',
    ticker_text: '',
    ticker_duration_hours: '24',
    popup_enabled: '0',
    popup_image: '',
    popup_text: '',
    popup_duration_seconds: '5',
    popup_remind_hours: '24'
  });
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/settings`);
      const data = await res.json();
      setSettings({
        site_name: data.site_name || '',
        site_logo: data.site_logo || '/logo.png',
        site_favicon: data.site_favicon || '/favicon.ico',
        site_font: data.site_font || "'Tajawal', sans-serif",
        site_language: data.site_language || 'ar',
        site_languages: data.site_languages || JSON.stringify(['ar', 'en']),
        siteDescription: data.siteDescription || '',
        site_keywords: data.site_keywords || '',
        contactEmail: data.contactEmail || '',
        contactPhone: data.contactPhone || '',
        address: data.address || '',
        facebookUrl: data.facebookUrl || '',
        twitterUrl: data.twitterUrl || '',
        instagramUrl: data.instagramUrl || '',
        youtubeUrl: data.youtubeUrl || '',
        tiktokUrl: data.tiktokUrl || '',
        whatsappUrl: data.whatsappUrl || '',
        callUrl: data.callUrl || '',
        snapchatUrl: data.snapchatUrl || '',
        headerColor: data.headerColor || '#ffffff',
        footerColor: data.footerColor || '#0F1B2D',
        secondaryColor: data.secondaryColor || '#C8953A',
        nav_links: data.nav_links || JSON.stringify(DEFAULT_NAV_LINKS),
        ticker_enabled: data.ticker_enabled || '0',
        ticker_text: data.ticker_text || '',
        ticker_duration_hours: data.ticker_duration_hours || '24',
        popup_enabled: data.popup_enabled || '0',
        popup_image: data.popup_image || '',
        popup_text: data.popup_text || '',
        popup_duration_seconds: data.popup_duration_seconds || '5',
        popup_remind_hours: data.popup_remind_hours || '24'
      });
    } catch (e) {
      // fallback to defaults
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIconMap();
    fetchSettings();
    // eslint-disable-next-line
  }, []);

  // تطبيق الخط المختار على مستوى body
  useEffect(() => {
    if (settings.site_font) {
      document.body.style.fontFamily = settings.site_font;
    }
  }, [settings.site_font]);

  // تطبيق اللغة المختارة على مستوى html
  useEffect(() => {
    if (settings.site_language) {
      document.documentElement.lang = settings.site_language;
      document.body.dir = settings.site_language === 'ar' ? 'rtl' : 'ltr';
    }
  }, [settings.site_language]);

  // تطبيق الفاڤيكون
  useEffect(() => {
    if (settings.site_favicon) {
      const faviconUrl = settings.site_favicon.startsWith('http')
        ? settings.site_favicon
        : `${process.env.REACT_APP_API_URL || ''}${settings.site_favicon}`;
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = faviconUrl;
    }
  }, [settings.site_favicon]);

  // تطبيق الألوان الديناميكية (رأس الصفحة، أسفل الصفحة، اللون التكميلي)
  useEffect(() => {
    const root = document.documentElement;
    if (settings.headerColor) root.style.setProperty('--header-bg', settings.headerColor);
    if (settings.footerColor) root.style.setProperty('--footer-bg', settings.footerColor);
    if (settings.secondaryColor) root.style.setProperty('--secondary', settings.secondaryColor);
    if (settings.headerColor) {
      root.style.setProperty('--header-text', getContrastText(settings.headerColor));
    }
    if (settings.footerColor) {
      root.style.setProperty('--footer-text', getContrastText(settings.footerColor));
    }
  }, [settings.headerColor, settings.footerColor, settings.secondaryColor]);

  const getNavLinks = useMemo(() => {
    try {
      const parsed = JSON.parse(settings.nav_links);
      if (!Array.isArray(parsed)) return DEFAULT_NAV_LINKS;
      return parsed
        .filter(link => link.enabled !== false)
        .map(link => ({
          ...link,
          iconElement: ICON_MAP[link.icon]
            ? React.createElement(ICON_MAP[link.icon])
            : getDefaultIcon(link.icon)
        }));
    } catch {
      return DEFAULT_NAV_LINKS;
    }
  }, [settings.nav_links]);

  return (
    <SiteSettingsContext.Provider value={{
      ...settings,
      loading,
      refetch: fetchSettings,
      navLinks: getNavLinks
    }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
